import { getJoinNamePrefix } from "config";
import { v4 as uuidv4 } from "uuid";

/**
 * Drops every join config whose operands are not both live, transitively.
 *
 * A join's operand is either a selected source table or the joinName of a join
 * defined earlier in the array, so liveness resolves in a single forward pass:
 * only a surviving join contributes its own name to the joins that follow it.
 * Returns joinConfigs itself when nothing was dropped, so callers can compare
 * by reference; validTableNames is not mutated.
 */
export const pruneJoinConfigs = (joinConfigs, validTableNames) => {
  const configs = joinConfigs ?? [];
  const liveNames = new Set(validTableNames);

  const kept = [];
  for (const config of configs) {
    if (!liveNames.has(config.leftTable) || !liveNames.has(config.rightTable)) {
      continue;
    }
    if (config.joinName) liveNames.add(config.joinName);
    kept.push(config);
  }

  return kept.length === configs.length ? joinConfigs : kept;
};

/**
 * Returns the internal name for a newly created join.
 *
 * A join is identified by this name for its whole life: later joins name it as
 * an operand, so it is generated once and never edited. The name the user
 * reads and changes is joinDisplayName.
 */
export const newJoinId = () => uuidv4();

/**
 * Returns the name a join is shown under.
 *
 * Joins saved before joinDisplayName existed carry their display name in
 * joinName, which is why that is the fallback.
 */
export const joinDisplayNameOf = (config) =>
  config?.joinDisplayName || config?.joinName || "";

/**
 * Returns the lowest-numbered join display name not held by a join at another
 * index.
 *
 * A null or undefined excludeIndex excludes nothing. The name comes from the
 * names in use rather than the list length, because joins are removed from the
 * middle of the list.
 */
export const nextJoinDisplayName = (joinConfigs, excludeIndex) => {
  const namesInUse = new Set(
    (joinConfigs ?? [])
      .filter((_config, index) => index !== excludeIndex)
      .map(joinDisplayNameOf)
  );

  let counter = 1;
  while (namesInUse.has(`${getJoinNamePrefix()}${counter}`)) {
    counter += 1;
  }
  return `${getJoinNamePrefix()}${counter}`;
};

/**
 * True when the joins fold the selected sources into exactly one result.
 *
 * A report's joins are applied in order and each produces a named result that
 * a later join may consume, so the report's data set is the single join whose
 * result nothing else consumes. Two joins that both read sources directly
 * leave two results and only the last is returned, silently dropping whatever
 * the other one contributed, so connectivity alone is not enough: there must be
 * one unconsumed join, and walking it must reach every selected source.
 */
export const joinsFormSingleResult = (selectedSources, joinConfigs) => {
  const sources = new Set(selectedSources ?? []);
  const joins = joinConfigs ?? [];
  if (sources.size <= 1) return joins.length === 0;

  const byName = new Map();
  for (const config of joins) {
    if (config.joinName) byName.set(config.joinName, config);
  }

  const consumed = new Set();
  for (const config of joins) {
    for (const operand of [config.leftTable, config.rightTable]) {
      if (byName.has(operand)) consumed.add(operand);
    }
  }

  const roots = joins.filter(
    (config) => config.joinName && !consumed.has(config.joinName)
  );
  if (roots.length !== 1) return false;

  const reached = new Set();
  const walked = new Set();
  const walk = (config) => {
    if (!config || walked.has(config.joinName)) return;
    walked.add(config.joinName);
    for (const operand of [config.leftTable, config.rightTable]) {
      if (!operand) continue;
      if (byName.has(operand)) walk(byName.get(operand));
      else reached.add(operand);
    }
  };
  walk(roots[0]);

  if (reached.size !== sources.size) return false;
  for (const source of sources) {
    if (!reached.has(source)) return false;
  }
  return true;
};
